<template>
  <div class="tender-container">
    <discard-changes ref="discardAlert" />

    <tender-head
      ref="tenderEditHead"
      :tenderGeneral="currentTender.general"
    >
      <template v-if="isPicView || isRepresentative">
        <button
          v-if="!isEditing"
          v-note="{text:noteText, enable:!editable}"
          type="button"
          class="btn btn--darkblue"
          :disabled="!editable || isTenderRepEditing"
          @click="goEdit()"
        >{{ $t('action.edit') }}</button>
        <template v-else>
          <button
            type="button"
            class="btn btn--darkblue"
            :disabled="!isPageDirty"
            @click="openModal()"
          >{{ $t('action.discard') }}</button>
          <button
            v-hkldreset
            type="button"
            class="btn btn--orange"
            :disabled="disableSave"
            @click="save()"
          >{{ $t('action.save') }}</button>
          <button
            v-hkldreset
            type="button"
            class="btn btn--orange"
            :disabled="disableSave"
            @click="submit()"
          >{{ previewButtonName }}</button>
        </template>
      </template>
    </tender-head>

    <TenderTabsContainer
      v-if="isEditing"
      :isTender="isTender"
      :tabsInvalid="tabsInvalid"
    >
      <template v-if="isPicView || isRepresentative">
        <button
          type="button"
          class="btn btn--darkblue tender-sticky-button mini-btn"
          :disabled="!isPageDirty"
          @click="openModal()"
        >{{ $t('action.discard') }}</button>
        <button
          v-hkldreset
          type="button"
          class="btn btn--orange tender-sticky-button mini-btn"
          :disabled="disableSave"
          @click="save()"
        >{{ $t('action.save') }}</button>
        <button
          v-hkldreset
          type="button"
          class="btn btn--orange tender-sticky-button mini-btn"
          :disabled="disableSave"
          @click="submit()"
        >{{ previewButtonName }}</button>
      </template>

      <General
        slot="general"
        ref="general_tab"
        :isTender="isTender"
      />
      <ScopeOfWorkEditor slot="scopeOfWork" />
      <ItemsServices
        slot="itemServices"
        ref="items_services_tab"
        :lockItems="lockItems"
      />
      <FileUpload
        slot="fileUpload"
        ref="file_uploader_tab"
        :attachment.sync="attachment"
        :showTenderHints="true"
        :metaData="{category: undefined}"
        :resourceId="currentTender.general.id"
        :overallDescription="true"
      />
      <TermsConditions
        slot="termsConditions"
        :isTender="isTender"
        :zipName="zipNames.terms"
      />
    </TenderTabsContainer>

    <TenderTabsContainer
      v-else
      :isTender="isTender"
    >
      <button
        v-if="isPicView || isRepresentative"
        v-note="{text:noteText, enable:!editable}"
        type="button"
        class="btn btn--darkblue"
        :disabled="!editable"
        @click="goEdit()"
      >{{ $t('action.edit') }}</button>

      <GeneralView
        v-if="tenderId === currentTender.general.id"
        slot="general"
      />
      <ScopeOfWork slot="scopeOfWork" />
      <ItemsServicesView slot="itemServices" />
      <Attachments
        slot="fileUpload"
        :files="attachmentFiles"
        :description="attachment.description"
        :zipName="zipNames.attachments"
      />
      <TermsConditions
        slot="termsConditions"
        :tenderId="tenderId"
        :isTender="isTender"
        :zipName="zipNames.terms"
      />
    </TenderTabsContainer>

    <TenderRepresentative
      v-show="showTenderRep"
      :isEditing.sync="isTenderRepEditing"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { provide, ref } from 'vue-demi'
import checkEditableMixin from '@/modules/tenders/checkEditableMixin'
import EventBus from '@/utils/eventBus'
import keepEditStatusMixin from '@/modules/tenders/keepEditStatusMixin'
import mutationTypes from '@/store/mutation-types'
import router from '@/router'
import tenderRepShowMixin from '@/modules/tenders/tenderRepShowMixin'
import { getAttachments, saveAttachment } from '@/api/docs/request'
import { tenderStatus } from '@/utils/locale'
import { validatePR } from '@/api/tenders/request'
import Attachments from '@/modules/tenders/view/Attachments'
import DiscardChanges from '@/modules/tenders/edit/DiscardChanges'
import FileUpload from '@/modules/FileUpload'
import General from '@/modules/tenders/edit/General'
import GeneralView from '@/modules/tenders/view/General'
import ItemsServices from '@/modules/tenders/edit/ItemsServices'
import ItemsServicesView from '@/modules/tenders/view/ItemsServices'
import ScopeOfWork from '@/modules/tenders/view/ScopeOfWork'
import ScopeOfWorkEditor from '@/modules/tenders/edit/ScopeOfWork'
import TenderHead from '@/modules/tenders/TenderHead'
import TenderRepresentative from '@/modules/tenders/view/TenderRepresentative'
import TenderTabsContainer from '@/modules/tenders/TenderTabsContainer'
import TermsConditions from '@/modules/tenders/TermsConditions'
import { TENDER_EDITING_PROVIDE } from './consts'

export default {
  components: {
    Attachments,
    DiscardChanges,
    FileUpload,
    General,
    GeneralView,
    ItemsServices,
    ItemsServicesView,
    ScopeOfWorkEditor,
    ScopeOfWork,
    TenderHead,
    TenderRepresentative,
    TenderTabsContainer,
    TermsConditions,
  },
  mixins: [checkEditableMixin, keepEditStatusMixin, tenderRepShowMixin],
  setup () {
    const isEditing = ref(false)
    const isTenderRepEditing = ref(false)
    provide(TENDER_EDITING_PROVIDE, isEditing)
    return {
      isEditing,
      isTenderRepEditing,
    }
  },
  data () {
    return {
      active: 'mobile-tabls-general',
      tenderId: null,
      // TODO @Gino temparily for tabs use, remove after vuex migrated
      attachment: {
        description: '',
        files: [],
      },
      attachmentFiles: [],
      // Non persistent values
      disableSaveCount: 0,
      homePageUrl: '/',
      showMessage: false,
      isInfo: true,
      isError: false,
      lockItems: false,
      message: '',
      tabsInvalid: {
        generalInvalid: false,
        sectionInvalid: false,
        termsInvalid: false,
      },
    }
  },
  computed: {
    ...mapGetters('tenders', ['currentTender', 'isPicView', 'isRepresentative', 'zipNames']),
    ...mapGetters('form', ['isPageDirty']),
    ...mapGetters('user', ['currentUser', 'isEngineer']),
    isTender () { return this.currentTender.general.tenderType === 'TENDER' },
    general () {
      return this.currentTender.general
    },
    previewButtonName () {
      if (this.isRepresentative) return this.$t('action.preview')
      return this.isTender ? this.$t('action.preview_submit') : this.$t('action.preview_send')
    },
    disableSave () {
      return this.disableSaveCount > 0
    },
    isDraft () {
      return this.general.status === tenderStatus.DRAFT
    },
    isRejected () {
      return this.general.status === tenderStatus.REJECTED
    },
    isCancelled () {
      return this.general.status === tenderStatus.CANCELLED
    },
  },
  async mounted () {
    this.tenderId = router.currentRoute.params.id
    await this.loadTender(this.tenderId)
    if (!this.validateRoute()) return
    // check edit button status
    if (!this.isCancelled && (this.isPicView || this.isRepresentative) && (this.isDraft || this.isRejected)) {
      await this.checkEditable(this.tenderId)
      const enableEditing = router.currentRoute.params.isEditing
      if (enableEditing) {
        this.isEditing = true
        this.keepEdit(this.tenderId, this.navToEditPage)
      }
    }
    this.attachment.description = this.currentTender.general.attachmentDescription || ''
    this.attachmentFiles = await getAttachments(this.tenderId)

    EventBus.$on('controls-hkld-dropzone-sending', () => {
      this.disableSaveCount++
    })
    EventBus.$on('controls-hkld-dropzone-complete', () => {
      if (this.disableSaveCount > 0) {
        this.disableSaveCount--
      }
    })
    $('#tender-tabs li a')[0].click()
  },
  destroyed () {
    EventBus.$off('controls-hkld-dropzone-sending')
    EventBus.$off('controls-hkld-dropzone-complete')
    this.clearEditingStatus()
  },
  methods: {
    ...mapActions('tenders', [
      'loadTender',
      'validateRoute',
      'saveCurrentTender',
    ]),
    ...mapMutations('tenders', {
      updateTenderPathField: mutationTypes.TENDER_UPDATE_PATH_FIELD,
      clearSectionForReference: mutationTypes.TENDER_CLEAR_SECTION_FOR_REFERENCE,
    }),
    openModal () {
      this.$refs.discardAlert.init()
    },

    validate () {
      this.tabsInvalid.generalInvalid = this.$refs.general_tab.validate()
      this.tabsInvalid.sectionInvalid = this.$refs.items_services_tab.validate()
      return (!(this.tabsInvalid.generalInvalid || this.tabsInvalid.sectionInvalid))
    },

    clearErrorClass () {
      $('.is-invalid-input').trigger('hidetooltip')
      this.$refs.general_tab.clearErrorClasses()
      this.$refs.items_services_tab.clearErrorClasses()
    },
    async goEdit () {
      await this.checkEditable(this.tenderId)
      if (!this.editable) return
      this.isEditing = true
      this.keepEdit(this.tenderId, this.navToEditPage)
    },
    async save () {
      this.tabsInvalid.generalInvalid = false
      this.tabsInvalid.sectionInvalid = false
      this.clearErrorClass()
      await this.$refs.general_tab.saveCocStandardAttachment()
      await this.$refs.general_tab.updateCurrentOptionalText()

      await saveAttachment(this.attachment)
      this.updateTenderPathField({
        path: ['general', 'attachmentDescription'],
        newValue: this.attachment.description,
      })
      this.attachmentFiles = await getAttachments(this.tenderId)
      if (this.general.prNumber && this.isPicView) {
        const validPR = await validatePR(this.tenderId, this.general.prNumber, this.general.tenderType, this.currentTender.purchaseRequisition.budgetBase, this.currentTender.purchaseRequisition.siteId)
        if (!validPR) {
          return false
        }
      }
      await this.saveCurrentTender()
      this.isEditing = false
      this.clearEditingStatus()
      return true
    },

    async submit () {
      if (this.validate()) {
        this.lockItems = true
        this.clearSectionForReference()
        const result = await this.save()
        if (result) {
          this.backToView()
        }
      }
    },
    backToView () {
      const name = this.isEngineer ? 'tenderDetailsView' : 'tenderDetailsViewExt'
      router.push({
        name,
        params: {
          isFromSubmit: true,
          id: this.tenderId,
        },
      })
    },
    navToEditPage () {
      const path = this.$route.path.replace('/edit', '')
      this.$router.replace({
        path: path,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/stickyBtn";
</style>
